import { PlusIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, TextField } from '@shared/ui';

import styles from './Searches.module.css';

/** Ключи примеров запроса. Тексты — в локалях, здесь только порядок. */
const EXAMPLE_KEYS = ['searches.new.example1', 'searches.new.example2', 'searches.new.example3'];

interface NewSearchFormProps {
  /** Предзаполнение: текст с главной, который не удалось отправить сразу. */
  initialValue?: string | undefined;
  pending: boolean;
  maxLength: number;
  onSubmit: (prompt: string | null) => void;
}

/**
 * Поле первого запроса и примеры (FE-150, MVP_PRD §20).
 *
 * Пустое состояние списка обязано содержать не только объяснение, но и место,
 * куда написать: «У вас пока нет запросов» с одной кнопкой оставляет
 * пользователя перед пустым экраном.
 *
 * Текст уходит в `initial_prompt` при создании и оттуда же первым сообщением
 * диалога — так он виден в чате и не теряется при сбое отправки.
 */
export function NewSearchForm({
  initialValue = '',
  pending,
  maxLength,
  onSubmit,
}: NewSearchFormProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);

  return (
    <form
      className={styles.newForm}
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = value.trim();
        onSubmit(trimmed === '' ? null : trimmed);
      }}
    >
      <div className={styles.newRow}>
        <TextField
          className={styles.newField}
          label={t('searches.new.label')}
          placeholder={t('searches.new.placeholder')}
          hint={t('searches.new.hint')}
          maxLength={maxLength}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
        />
        {/* Главное действие экрана — единственная красная кнопка (DESIGN_SYSTEM §5.1). */}
        <Button
          type="submit"
          disabled={pending}
          className={styles.newSubmit}
          iconEnd={<PlusIcon size={16} weight="bold" />}
        >
          {pending ? t('searches.new.pending') : t('searches.new.submit')}
        </Button>
      </div>

      <ul className={styles.examples} aria-label={t('searches.new.examples')}>
        {EXAMPLE_KEYS.map((key) => (
          <li key={key}>
            <button
              type="button"
              className={styles.example}
              onClick={() => {
                setValue(t(key));
              }}
            >
              {t(key)}
            </button>
          </li>
        ))}
      </ul>
    </form>
  );
}
